import * as THREE from 'three';
import type { MapDefinition, MapInterior } from '../../../shared/game/world-types.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import { caveAround, levelMap } from '../../../shared/game/caves.js';

/** Sunk rock stands this high over the nearest floor, rising this many metres per metre further from it, back to its own height. */
export const WALL_HEIGHT = 2.5, WALL_SLOPE = .25;
const CELL = 1, MARGIN = 36, SINK_SECONDS = .45;
// Scree fanning from a wall's top into the floor ring is rock, not floor: floor lies within this of the lowest ground nearby.
const FLOOR_SPREAD = 1.5, FLOOR_REACH = 6;
// No rock lies more than this above its sunk height, so cells beyond an interior's reach keep a smooth, unclamped value.
const UNCLAMPED = 4;
const adopted = new WeakSet<THREE.Material>();
const uniforms = {
  wallsDownMap: { value: null as THREE.DataTexture | null }, wallsDownBounds: { value: new THREE.Vector4() },
  wallsDownSize: { value: new THREE.Vector2() }, wallsDownAmount: { value: 0 },
};

const SAMPLE = `
uniform highp sampler2D wallsDownMap;
uniform vec4 wallsDownBounds;
uniform vec2 wallsDownSize;
uniform float wallsDownAmount;
// x: the sunk rock's height, y: the real ground's, bilinear over the metre grid.
vec2 wallsDownAt(vec2 xz) {
  vec2 cell = clamp((xz - wallsDownBounds.xy) * wallsDownBounds.z, vec2(0.), wallsDownSize - 1.001);
  ivec2 i = ivec2(floor(cell)); vec2 f = cell - floor(cell);
  vec2 a = texelFetch(wallsDownMap, i, 0).xy, b = texelFetch(wallsDownMap, i + ivec2(1, 0), 0).xy;
  vec2 c = texelFetch(wallsDownMap, i + ivec2(0, 1), 0).xy, d = texelFetch(wallsDownMap, i + ivec2(1, 1), 0).xy;
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
bool wallsDownCovers(vec2 xz) {
  return wallsDownAmount > 0. && all(greaterThanEqual(xz, wallsDownBounds.xy)) && all(lessThan(xz, wallsDownBounds.xy + wallsDownSize / wallsDownBounds.z));
}
`;

interface Grid { x0: number; z0: number; columns: number; rows: number; slice: Float32Array; ground: Float32Array; floor: Uint8Array }

/**
 * Presentation only: while the viewer's heroes stand on an interior's floor (a cave warren cut into high ground), the rock
 * round its floors sinks to a low darkened wall, so the warren reads as a plan from the fixed camera. Shadows, rules,
 * navigation and vision keep the real ground; props over sunk rock hide, and ground clicks follow the sunk surface.
 */
export class WallsDown {
  private grid: Grid | null = null;
  private texture: THREE.DataTexture | null = null;
  private amount = 0;
  private terrain = new WeakSet<THREE.Material>();
  readonly lowered = new Map<THREE.Object3D, number>();

  constructor(map: MapDefinition) {
    if (!map.interiors?.length) return;
    this.grid = buildGrid(map, map.interiors);
    const { columns, rows, slice, ground, x0, z0 } = this.grid, data = new Float32Array(columns * rows * 4);
    for (let k = 0; k < slice.length; k++) { data[k * 4] = slice[k]; data[k * 4 + 1] = ground[k]; }
    this.texture = new THREE.DataTexture(data, columns, rows, THREE.RGBAFormat, THREE.FloatType);
    this.texture.needsUpdate = true;
    uniforms.wallsDownMap.value = this.texture;
    uniforms.wallsDownBounds.value.set(x0, z0, 1 / CELL, 0);
    uniforms.wallsDownSize.value.set(columns, rows);
    uniforms.wallsDownAmount.value = 0;
  }

  get active() { return this.amount > 0; }

  /** The ground sinks in the vertex shader, its rock flattened and darkened where it was cut. */
  adoptTerrain(material: THREE.Material) {
    if (!this.grid || adopted.has(material)) return;
    adopted.add(material); this.terrain.add(material);
    const compile = material.onBeforeCompile, key = material.customProgramCacheKey.call(material);
    material.onBeforeCompile = function (this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) {
      compile.call(this, shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = SAMPLE + 'varying float vWallsCut;\n' + shader.vertexShader
        .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
  vec3 wallsWorld = (modelMatrix * vec4(position, 1.)).xyz;
  float wallsLift = wallsDownCovers(wallsWorld.xz) ? max(0., wallsWorld.y - wallsDownAt(wallsWorld.xz).x) * wallsDownAmount : 0.;
  vWallsCut = wallsLift;
  // The cut top faces the sky, whatever slope it was cut from.
  if (wallsLift > 0.) objectNormal = normalize(mix(objectNormal, vec3(0., 1., 0.), min(1., wallsLift)));`)
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  transformed.y -= wallsLift;');
      shader.fragmentShader = 'varying float vWallsCut;\n' + shader.fragmentShader
        .replace('#include <opaque_fragment>', '#include <opaque_fragment>\n  gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * vec3(.5, .46, .44), smoothstep(0., 1.5, vWallsCut));');
    };
    material.customProgramCacheKey = () => key + '|walls-down';
    material.needsUpdate = true;
  }

  /** Scenery standing over sunk rock hides; scenery on the floors stays, however tall. */
  adoptScenery(root: THREE.Object3D) {
    if (!this.grid) return;
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh)) return;
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        if (adopted.has(material) || this.terrain.has(material) || material.transparent || !material.colorWrite) continue;
        adopted.add(material);
        const compile = material.onBeforeCompile, key = material.customProgramCacheKey.call(material);
        material.onBeforeCompile = function (this: THREE.Material, shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer) {
          compile.call(this, shader, renderer);
          Object.assign(shader.uniforms, uniforms);
          // World position from the final view position, after instancing, batching and wind.
          const end = shader.vertexShader.lastIndexOf('}');
          shader.vertexShader = 'varying vec3 vWallsWorld;\n' + shader.vertexShader.slice(0, end)
            + '\nvWallsWorld = (vec4(mvPosition.xyz - viewMatrix[3].xyz, 0.) * viewMatrix).xyz;\n}';
          shader.fragmentShader = SAMPLE + 'varying vec3 vWallsWorld;\n' + shader.fragmentShader
            .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  if (wallsDownCovers(vWallsWorld.xz)) {
    vec2 wallsAt = wallsDownAt(vWallsWorld.xz);
    if (wallsAt.y - wallsAt.x > .5 && vWallsWorld.y > wallsAt.x + (wallsAt.y - wallsAt.x) * (1. - wallsDownAmount) - .05) discard;
  }`);
        };
        material.customProgramCacheKey = () => key + '|walls-down';
        material.needsUpdate = true;
      }
    });
  }

  /** Whether a hero standing here is down on an interior floor, not on the rock above it. */
  inside(x: number, z: number, y: number) {
    const grid = this.grid;
    if (!grid) return false;
    const i = Math.round((x - grid.x0) / CELL), j = Math.round((z - grid.z0) / CELL);
    if (i < 0 || j < 0 || i >= grid.columns || j >= grid.rows) return false;
    const k = j * grid.columns + i;
    return grid.floor[k] === 1 && y < grid.slice[k];
  }

  /** Eases the rock down while any of `heroes` stands on a floor, and back up once none does. */
  update(dt: number, heroes: Iterable<THREE.Vector3>) {
    if (!this.grid) return;
    let inside = false;
    for (const position of heroes) if (this.inside(position.x, position.z, position.y)) { inside = true; break; }
    this.amount = THREE.MathUtils.clamp(this.amount + (inside ? dt : -dt) / SINK_SECONDS, 0, 1);
    uniforms.wallsDownMap.value = this.texture;
    uniforms.wallsDownAmount.value = this.amount * this.amount * (3 - 2 * this.amount);
  }

  /** The height ground at (x, z) is drawn at: picking uses it so clicks land on the sunk rock, not the real one. */
  surface(x: number, z: number, height: number) {
    const grid = this.grid, amount = uniforms.wallsDownAmount.value;
    if (!grid || amount <= 0) return height;
    const slice = sample(grid, grid.slice, x, z);
    return slice === null ? height : height - Math.max(0, height - slice) * amount;
  }

  /**
   * Bodies (units, critters) standing on sunk rock are drawn on its new top for the frame; `restore` puts them back before
   * anything reads their positions again.
   */
  lower(objects: Iterable<THREE.Object3D>) {
    const grid = this.grid, amount = uniforms.wallsDownAmount.value;
    if (!grid || amount <= 0) return;
    for (const object of objects) {
      const { x, y, z } = object.position, ground = sample(grid, grid.ground, x, z), slice = sample(grid, grid.slice, x, z);
      if (ground === null || slice === null || ground - slice < .5 || Math.abs(y - ground) > 1.5) continue;
      const drop = (ground - slice) * amount;
      object.position.y -= drop; this.lowered.set(object, drop);
    }
  }

  restore() {
    for (const [object, drop] of this.lowered) object.position.y += drop;
    this.lowered.clear();
  }

  dispose() {
    if (uniforms.wallsDownMap.value === this.texture) { uniforms.wallsDownMap.value = null; uniforms.wallsDownAmount.value = 0; }
    this.texture?.dispose();
  }
}

/** The lowest value within `reach` cells along each axis (a square window), in two separable passes. */
function minimumFilter(values: Float32Array, columns: number, rows: number, reach: number) {
  const across = new Float32Array(values.length), out = new Float32Array(values.length);
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    let low = Infinity;
    for (let n = Math.max(0, i - reach); n <= Math.min(columns - 1, i + reach); n++) low = Math.min(low, values[j * columns + n]);
    across[j * columns + i] = low;
  }
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    let low = Infinity;
    for (let n = Math.max(0, j - reach); n <= Math.min(rows - 1, j + reach); n++) low = Math.min(low, across[n * columns + i]);
    out[j * columns + i] = low;
  }
  return out;
}

function sample(grid: Grid, values: Float32Array, x: number, z: number) {
  const u = (x - grid.x0) / CELL, v = (z - grid.z0) / CELL;
  if (u < 0 || v < 0 || u > grid.columns - 1 || v > grid.rows - 1) return null;
  const i = Math.min(grid.columns - 2, Math.floor(u)), j = Math.min(grid.rows - 2, Math.floor(v)), fu = u - i, fv = v - j, k = j * grid.columns + i;
  const top = values[k] + (values[k + 1] - values[k]) * fu, bottom = values[k + grid.columns] + (values[k + grid.columns + 1] - values[k + grid.columns]) * fu;
  return top + (bottom - top) * fv;
}

/**
 * Sunk heights on a metre grid: each floor cell seeds its own height plus the wall, and the rock between takes the lowest
 * seed plus `WALL_SLOPE` per metre away (a two-pass chamfer), so rock far from any floor keeps its real height.
 */
export function buildGrid(map: MapDefinition, interiors: MapInterior[]): Grid {
  const rings = interiors.flatMap(interior => interior.floors), points = rings.flat();
  const x0 = Math.floor(Math.min(...points.map(p => p.x)) - MARGIN), z0 = Math.floor(Math.min(...points.map(p => p.z)) - MARGIN);
  const columns = Math.ceil(Math.max(...points.map(p => p.x)) + MARGIN - x0) + 1, rows = Math.ceil(Math.max(...points.map(p => p.z)) + MARGIN - z0) + 1;
  const ground = new Float32Array(columns * rows), slice = new Float32Array(columns * rows).fill(Infinity), floor = new Uint8Array(columns * rows);
  // Under a cave's roof the ground is the cave's floor, not the rock's top.
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const x = x0 + i * CELL, z = z0 + j * CELL;
    ground[j * columns + i] = terrainHeight(levelMap(map, caveAround(map, x, z)), x, z);
  }
  // Floors, row by row with the even-odd rule, so rock islands stay rock.
  for (let j = 0; j < rows; j++) {
    const z = z0 + j * CELL, crossings: number[] = [];
    for (const ring of rings) ring.forEach((a, n) => {
      const b = ring[(n + 1) % ring.length];
      if ((a.z > z) !== (b.z > z)) crossings.push(a.x + (z - a.z) / (b.z - a.z) * (b.x - a.x));
    });
    crossings.sort((a, b) => a - b);
    for (let n = 0; n + 1 < crossings.length; n += 2)
      for (let i = Math.max(0, Math.ceil((crossings[n] - x0) / CELL)); i <= Math.min(columns - 1, Math.floor((crossings[n + 1] - x0) / CELL)); i++) floor[j * columns + i] = 1;
  }
  const lowest = minimumFilter(ground, columns, rows, Math.round(FLOOR_REACH / CELL));
  for (let k = 0; k < floor.length; k++) {
    if (floor[k] && ground[k] > lowest[k] + FLOOR_SPREAD) floor[k] = 0;
    if (floor[k]) slice[k] = ground[k] + WALL_HEIGHT;
  }
  const straight = WALL_SLOPE * CELL, diagonal = WALL_SLOPE * CELL * Math.SQRT2;
  const relax = (k: number, from: number, cost: number) => { if (slice[from] + cost < slice[k]) slice[k] = slice[from] + cost; };
  for (let j = 0; j < rows; j++) for (let i = 0; i < columns; i++) {
    const k = j * columns + i;
    if (i > 0) relax(k, k - 1, straight);
    if (j > 0) { relax(k, k - columns, straight); if (i > 0) relax(k, k - columns - 1, diagonal); if (i < columns - 1) relax(k, k - columns + 1, diagonal); }
  }
  for (let j = rows - 1; j >= 0; j--) for (let i = columns - 1; i >= 0; i--) {
    const k = j * columns + i;
    if (i < columns - 1) relax(k, k + 1, straight);
    if (j < rows - 1) { relax(k, k + columns, straight); if (i < columns - 1) relax(k, k + columns + 1, diagonal); if (i > 0) relax(k, k + columns - 1, diagonal); }
  }
  // Floors never sink (a passage beside a lower chamber keeps its height), and rock out of reach keeps a finite, smooth value.
  for (let k = 0; k < slice.length; k++) slice[k] = Math.min(floor[k] ? Math.max(slice[k], ground[k] + WALL_HEIGHT) : slice[k], ground[k] + UNCLAMPED);
  return { x0, z0, columns, rows, slice, ground, floor };
}

